import React, { useState, useEffect } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Modal,
    Tooltip
} from '@mui/material';
import CreatableSelect from "react-select/creatable";
import makeAnimated from "react-select/animated";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { getData, postData, putData, deleteData } from '../../../webService/webservice.js';
import '../../../styles/Auth/expense.css';
import TVFMLoader from "../Loader/loader.jsx";
import { useStore } from "../../../Context/context.jsx";
import AddStoreForm from "../store/AddStoreForm.jsx";
import StoreMultiSelect from "../../Challange/storeMultiSelect.jsx";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import { Icon } from "@iconify/react";
import EmployeeSelect from "../../Challange/EmployeeSelect.jsx";
import AddEmployeeForm from "../Employee/AddEmployeeForm.jsx";
import Footer from "../Footer/Footer-page.jsx";
import plusIcon from '@iconify/icons-mdi/eye-circle-outline';
import deleteIcon from '@iconify/icons-mdi/trash-can-outline';
import editIcon from '@iconify/icons-mdi/square-edit-outline';
import ExpenseDetailsPopup from './ExpenseDetailsPopup.jsx';


const ExpensePage = ({
    expenses,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    limit,
    setLimit,
    store,
    onRefresh,
    sortField,
    sortOrder,
    handleSort
}) => {
    const commonCellStyle = {
        color: '#525252',
        fontSize: '12px',
        fontWeight: 600,
        textAlign: 'center'
    };
    const headings = [
        'S.NO ',
        'Expense',
        'Description',
        'Employee',
        'Date',
        'Amount',
        'Payment Mode',
        'Expense Type',
        'Invoice',
        'Action'
    ];

    const [users, setUsers] = useState([]);

    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const initialStoreId = adminStoreIds[0] || "";
    const [stores, setStores] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddExpense, setIsAddExpense] = useState(false);
    const [isAddStore, setIsAddStore] = useState(false);
    const [isAddEmployee, setIsAddEmployee] = useState(false);
    const [employeeRefreshKey, setEmployeeRefreshKey] = useState(0);
    const [storeRefreshKey, setStoreRefreshKey] = useState(0);
    const { selectedStoreId, setSelectedStoreId } = useStore();
    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({});
    const [showLoader, setShowLoader] = useState(false);
    const [loaderText, setLoaderText] = useState("Adding Expense...");
    const [isEditMode, setIsEditMode] = useState(false);
    const [editExpenseId, setEditExpenseId] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [expenseToDelete, setExpenseToDelete] = useState(null);
    const [isViewMode, setIsViewMode] = useState(false);
    const [viewExpenseData, setViewExpenseData] = useState(null);
    const sessionToken = localStorage.getItem("Token");
    const [formData, setFormData] = useState({
        expenseName: '',
        otherExpenseName: '', // Added for 'Other' selection
        description: '',
        employeeId: '',
        amount: "",
        date: "",
        expenseType: "",
        invoiceNo: "",
        paymentMode: "",
        storeId: initialStoreId,
        storeName: "", // Initialize storeName
    });

    useEffect(() => {
        const fetchStoreName = async () => {
            // Handle single storeId
            if (formData.storeId && !formData.storeName) {
                try {
                    const res = await getData(`stores/store/${formData.storeId}`, {}, sessionToken);
                    if (res?.data) {
                        setFormData(prev => ({
                            ...prev,
                            storeName: res.data.storeName
                        }));
                    }
                } catch (err) {
                    console.error("Failed to fetch store name", err);
                }
            }
        };
        fetchStoreName();
    }, [formData.storeId, formData.storeName, isAddStore, isAddExpense]);



    const triggerRefresh = () => {
        if (onRefresh) onRefresh();
    };

    const EMPTY_ARRAY = [];

    const loadStores = async (inputValue, loadedOptions, { page }) => {
        try {
            const res = await getData(
                "stores/stores",
                { page, limit: 10, search: inputValue },
                sessionToken
            );

            const results = res?.data?.results || EMPTY_ARRAY;

            const options = results.map(store => ({
                value: store._id,
                label: store.storeName
            }));

            return {
                options,
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };

        } catch (err) {
            console.error("Store Fetch Failed", err);
            return { options: [], hasMore: false };
        }
    };

    const loadEmployees = async (inputValue, loadedOptions, { page }) => {
        try {
            const token = localStorage.getItem("Token");
            const params = {
                page,
                limit: 10,
                search: inputValue,
                storeId: formData.storeId,
            }
            const res = await getData('users/users', params, token);
            const results = res?.data?.[0]?.paginatedResults || [];

            const options = results.map(user => ({
                value: user._id,
                label: user.name
            }));

            return {
                options,
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };

        } catch (err) {
            console.error("Employee Fetch Failed", err);
            return { options: [], hasMore: false };
        }
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "next") return Math.min(prev + 1, totalPages);
            if (direction === "prev") return Math.max(prev - 1, 1);
            if (direction === "first") return 1;
            if (direction === "last") return totalPages;
            return prev;
        });
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        const newLimit = value === "all" ? "all" : Number(value);
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const expenseOptions = [
        { value: 'salary', label: 'Salary' },
        { value: 'incentives', label: 'Incentives' },
        { value: 'OT', label: 'OT' },
        { value: 'recreation', label: 'Recreation' },
        { value: 'repair & maintanence', label: 'Repair & Maintanence' },
        { value: 'purchase', label: 'Purchase' },
        { value: 'rental', label: 'Rental' },
        { value: 'opening & closing balance', label: 'Opening & Closing Balance' },
        { value: 'EB', label: 'EB' },
        { value: 'phone bill', label: 'Phone Bill' },
        { value: 'royalty', label: 'Royalty' },
        { value: 'GST', label: 'GST' },
        { value: 'miscellenous', label: 'Miscellenous' },
        { value: 'other', label: 'Other' },
    ];

    const handleAddExpense = async (e) => {
        e.preventDefault();
        console.log("✅ handleAddExpense triggered");

        const validationErrors = validateFields(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});
        setShowLoader(true);
        setLoaderText("Adding Expense...");

        try {
            const token = localStorage.getItem("Token");

            const expenseData = {
                storeId: formData.storeId,
                expenseName: formData.expenseName === 'other' ? formData.otherExpenseName : formData.expenseName,
                description: formData.description,
                employeeId: formData.employeeId || null,
                date: formData.date,
                paymentMode: formData.paymentMode,
                amount: formData.amount,
                expenseType: formData.expenseType,
                invoiceNo: formData.invoiceNo
            };

            console.log("Sending Expense data:", expenseData);

            const response = await postData("expense/", expenseData, token);
            toast.success(response.message || "Expense added successfully");

            setFormData({
                expenseName: "",
                otherExpenseName: "",
                description: "",
                amount: "",
                date: "",
                expenseType: "",
                invoiceNo: "",
                paymentMode: "",
            });
            setIsAddExpense(false);
            triggerRefresh();

        } catch (error) {
            console.error("Add Expense Error:", error);
            toast.error(error.message || "Add Expense Failed");
        } finally {
            setTimeout(() => {
                setShowLoader(false);
            }, 2000);
        }
    };

    const handleUpdateExpense = async (e) => {
        e.preventDefault();
        console.log("✅ handleUpdateExpense triggered");

        const validationErrors = validateFields(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});
        setShowLoader(true);
        setLoaderText("Updating Expense...");

        try {
            const token = localStorage.getItem("Token");

            const expenseData = {
                storeId: formData.storeId,
                expenseName: formData.expenseName === 'other' ? formData.otherExpenseName : formData.expenseName,
                description: formData.description,
                employeeId: formData.employeeId || null,
                date: formData.date,
                paymentMode: formData.paymentMode,
                amount: formData.amount,
                expenseType: formData.expenseType,
                invoiceNo: formData.invoiceNo
            };

            const response = await putData(`expense/expense/${editExpenseId}`, expenseData, token);
            toast.success(response.message || "Expense updated successfully");
            setFormData({
                expenseName: "",
                otherExpenseName: "",
                description: "",
                amount: "",
                date: "",
                expenseType: "",
                invoiceNo: "",
                paymentMode: "",
            });
            setIsAddExpense(false);
            setIsEditMode(false);
            setEditExpenseId(null);
            triggerRefresh();

        } catch (error) {
            console.error("Update Expense Error:", error);
            toast.error(error.message || "Update Failed");
        } finally {
            setTimeout(() => {
                setShowLoader(false);
            }, 2000);
        }
    };

    const handleEdit = (expense) => {
        const isStandard = expenseOptions.some(opt => opt.value === expense.expenseName);

        setFormData({
            expenseName: isStandard ? expense.expenseName : 'other',
            otherExpenseName: isStandard ? '' : expense.expenseName,
            description: expense.description || '',
            employeeId: expense.employeeId?._id || expense.employeeId?.[0]?._id || expense.employeeDetails?._id || expense.employeeId || '',
            employeeName: expense.employeeId?.name || expense.employeeDetails?.name || expense.employeeId?.[0]?.name || '',
            amount: expense.amount || '',
            date: expense.date ? expense.date.split('T')[0] : '',
            expenseType: expense.expenseType || '',
            invoiceNo: expense.invoiceNo || '',
            paymentMode: expense.paymentMode || '',
            storeId: expense.storeId?._id || expense.storeId || initialStoreId,
            storeName: expense.storeId?.storeName || '',
        });
        setEditExpenseId(expense._id);
        setIsEditMode(true);
        setIsAddExpense(true);
        setLoaderText("Updating Expense...");
    };

    const handleView = (expense) => {
        setViewExpenseData(expense);
        setIsViewMode(true);
    };

    const handleOpenDelete = (id) => {
        setExpenseToDelete(id);
        setOpenDeleteModal(true);
    };

    const handleCloseDelete = () => {
        setOpenDeleteModal(false);
        setExpenseToDelete(null);
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem("Token");
            await deleteData(`expense/expense/${expenseToDelete}`, token);
            toast.success("Expense deleted successfully");
            handleCloseDelete();
            triggerRefresh();
        } catch (error) {
            console.error("Delete Error:", error);
            toast.error(error.message || "Delete Failed");
        }
    };


    useEffect(() => {
        if (!formData.date) {
            const today = new Date().toISOString().split("T")[0]; // yyyy-mm-dd
            setFormData((prev) => ({ ...prev, date: today }));
        }
    }, [formData.date]);

    useEffect(() => {
        const handleStoreChange = () => {
            const storeId = selectedStoreId;
            setSelectedStoreId(storeId);
            setCurrentPage(1);
        };
        setLoading(true);
        window.addEventListener("storeChanged", handleStoreChange);
        return () => window.removeEventListener("storeChanged", handleStoreChange);
    }, []);

    const validateFields = (data) => {
        let newErrors = {};
        if (!data.expenseType) newErrors.expenseType = "Expense Type is required";
        if (!data.date) newErrors.date = "Date is required";
        if (!data.expenseName) newErrors.expenseName = "expenseName is required";
        if (data.expenseName === 'other' && !data.otherExpenseName) newErrors.otherExpenseName = "Please specify the expense name";
        if (!data.amount) newErrors.amount = "amount is required";
        if (!data.paymentMode) newErrors.paymentMode = "Payment Mode is required";
        if (!data.description) newErrors.description = "Description is required";
        return newErrors;
    }

    useEffect(() => {
        const handleStoreChange = () => {
            const storeId =
                selectedStoreId ||
                adminStoreIds[0] ||
                "";

            setSelectedStoreId(storeId);
            setCurrentPage(1);
        };

        // 🔥 IMPORTANT: run once on mount
        handleStoreChange();

        window.addEventListener("storeChanged", handleStoreChange);

        return () => {
            window.removeEventListener("storeChanged", handleStoreChange);
        };
    }, []);

    const handleExpenseDateChangeWithYearCheck = (value) => {
        if (!value) {
            setFormData(prev => ({ ...prev, date: "" }));
            return;
        }

        // Expected format: YYYY-MM-DD
        const year = value.split("-")[0];

        if (year.length > 4) {
            toast.error("Year should contain only 4 digits");
            return; // 🚫 block invalid update
        }

        setFormData(prev => ({
            ...prev,
            date: value
        }));
    };


    return (
        <div>
            <div>
                {
                    showLoader && (
                        <div className="scissor-overlay">
                            <div className="scissor-loader"><TVFMLoader /></div>
                        </div>
                    )
                }
                <main className='product-main'>
                    {isAddExpense && !isEditMode ? (
                        <div className='add-Expense-form'>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Add Expense
                            </Typography>
                            <form className='Expense-form' onSubmit={handleAddExpense}>
                                <div className='Expense-form-row'>
                                    <div className="Expense-form-group">
                                        <label className="input-label">Store Name</label>
                                        <StoreMultiSelect
                                            value={
                                                formData.storeId
                                                    ? [{
                                                        value: formData.storeId,
                                                        label: formData.storeName
                                                    }]
                                                    : []
                                            }
                                            onChange={(val) => {
                                                // ❌ Prevent clearing if only one store
                                                if (adminStoreIds.length === 1) return;

                                                setFormData(prev => ({
                                                    ...prev,
                                                    storeId: val?.[0]?.value || "",
                                                    storeName: val?.[0]?.label || "",

                                                }));
                                            }}
                                            loadStores={loadStores}
                                            loggedInRole={role}
                                            assignedStoreIds={adminStoreIds}
                                            isDisabled={adminStoreIds.length === 1}   // 🔥 KEY LINE
                                        />
                                    </div>
                                    {/* ================= EXPENSE FIELD ================= */}
                                    <div className="Expense-form-group">
                                        <label className="input-label">Expense</label>

                                        <CreatableSelect
                                            components={makeAnimated()}
                                            options={expenseOptions}
                                            placeholder="Select or type expense"
                                            isClearable
                                            isSearchable

                                            value={
                                                expenseOptions.find(option => option.value === formData.expenseName)
                                                || (formData.expenseName
                                                    ? { value: formData.expenseName, label: formData.expenseName }
                                                    : null)
                                            }

                                            onChange={(selectedOption) => {
                                                const selectedValue = selectedOption ? selectedOption.value : "";

                                                setFormData(prev => ({
                                                    ...prev,
                                                    expenseName: selectedValue,

                                                    // ✅ CLEAR EMPLOYEE IF NOT REQUIRED
                                                    employeeId: ["salary", "incentives", "OT"].includes(selectedValue)
                                                        ? prev.employeeId
                                                        : "",
                                                    employeeName: ["salary", "incentives", "OT"].includes(selectedValue)
                                                        ? prev.employeeName
                                                        : ""
                                                }));
                                            }}
                                        />

                                        {errors.expenseName && (
                                            <p className="field-error">{errors.expenseName}</p>
                                        )}
                                    </div>

                                    {/* ================= OTHER EXPENSE NAME FIELD ================= */}
                                    {formData.expenseName === 'other' && (
                                        <div className="Expense-form-group">
                                            <label className="input-label">Other Expense Name</label>
                                            <input
                                                className="Expense-input"
                                                placeholder="Specify the expense"
                                                value={formData.otherExpenseName || ""}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    otherExpenseName: e.target.value
                                                }))}
                                            />
                                            {errors.otherExpenseName && (
                                                <p className="field-error">{errors.otherExpenseName}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* ================= EMPLOYEE FIELD (CONDITIONAL + DROPDOWN) ================= */}
                                    {["salary", "incentives", "OT"].includes(formData.expenseName) && (
                                        <div className="Expense-form-group">
                                            <label className="input-label">Employee</label>

                                            <EmployeeSelect
                                                key={`${formData.storeId}-${employeeRefreshKey}`}
                                                loadEmployees={loadEmployees}
                                                value={
                                                    formData.employeeId
                                                        ? {
                                                            value: formData.employeeId,
                                                            label: formData.employeeName
                                                        }
                                                        : null
                                                }
                                                onAddEmployee={() => setIsAddEmployee(true)}
                                                onChange={(selectedOption) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        employeeId: selectedOption ? selectedOption.value : "",
                                                        employeeName: selectedOption ? selectedOption.label : ""
                                                    }));
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div className="Expense-form-group">
                                        <label className="input-label">Date</label>
                                        <input
                                            type="date"
                                            className="Expense-input"
                                            value={formData.date || ""}
                                            disabled={!formData.expenseName}
                                            onChange={(e) =>
                                                handleExpenseDateChangeWithYearCheck(e.target.value)
                                            }
                                        />

                                        {errors.date && <p className="field-error">{errors.date}</p>}
                                    </div>
                                </div>
                                <div className='Expense-form-row'>
                                    <div className='Expense-form-group'>
                                        <label className="input-label">Expense Type</label>
                                        <select
                                            id="expenseType"
                                            className="Expense-input"
                                            required
                                            disabled={!formData.expenseName}
                                            value={formData.expenseType}
                                            onChange={(e) => {
                                                const selectedUnitType = e.target.value;
                                                setFormData((prev) => ({ ...prev, expenseType: selectedUnitType }))
                                            }}
                                        >
                                            <option value='' >Select Expense Type</option>
                                            <option value='Billable'>Receiptable</option>
                                            <option value='Non-Billable'>Non-Receiptable</option>
                                        </select>
                                        {errors.expenseType && <p className="field-error">{errors.expenseType}</p>}
                                    </div>
                                    {formData.expenseType === "Billable" && (
                                        <div className='Expense-form-group'>
                                            <label className='input-label'>Invoice No</label>
                                            <input
                                                className="Expense-input"
                                                value={formData.invoiceNo || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*$/.test(value)) { // only digits allowed
                                                        setFormData((prev) => ({ ...prev, invoiceNo: value }));
                                                    }
                                                }}
                                            />
                                            {errors.invoiceNo && <p className="field-error">{errors.invoiceNo}</p>}
                                        </div>
                                    )}
                                    <div className='Expense-form-group'>
                                        <label className='input-label'>Amount</label>
                                        <input
                                            className="Expense-input"
                                            disabled={!formData.expenseType}
                                            value={formData.amount || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                // Allow only numbers with optional decimal point
                                                if (/^\d*\.?\d{0,2}$/.test(value)) {
                                                    setFormData((prev) => ({ ...prev, amount: value }));
                                                }
                                            }}
                                        />
                                        {errors.amount && <p className="field-error">{errors.amount}</p>}
                                    </div>
                                    <div className='Expense-form-group'>
                                        <label className='input-label'>Payment Mode</label>
                                        <select
                                            id="paymentMode"
                                            className="Expense-input"
                                            required
                                            disabled={!formData.amount}
                                            value={formData.paymentMode}
                                            onChange={(e) => {
                                                const selectedUnitType = e.target.value;
                                                setFormData((prev) => ({ ...prev, paymentMode: selectedUnitType }))
                                            }}
                                        >
                                            <option value='' >Select Payment Mode</option>
                                            <option value='CASH'>Cash</option>
                                            <option value='CARD'>Card</option>
                                            <option value='UPI'>UPI</option>
                                            <option value='OTHERS'>Others</option>
                                        </select>
                                        {errors.paymentMode && <p className="field-error">{errors.paymentMode}</p>}
                                    </div>
                                </div>
                                <div className='Expense-form-row'>
                                    <div className='Expense-form-group'>
                                        <label className='input-label'>Description</label>
                                        <textarea
                                            placeholder="Enter description"
                                            className="Expense-input"
                                            value={formData.description || ""}
                                            onChange={e => {
                                                let value = e.target.value;

                                                // Auto bullet on Enter
                                                if (value.endsWith("\n")) {
                                                    value += "• ";
                                                }
                                                setFormData(prev => ({ ...prev, description: value }));
                                            }}
                                            style={{ height: '100px' }}
                                        />
                                        {errors.description && <p className="field-error">{errors.description}</p>}
                                    </div>
                                </div>
                                <div className="Expense-buttons">
                                    <button className="add-Product-button" type="submit">
                                        Add Expense
                                    </button>
                                    <button type="button" className="cancel-Product-button" onClick={() => {
                                        setIsAddExpense(false);
                                        setFormData({
                                            expenseName: "",
                                            otherExpenseName: "",
                                            description: "",
                                            amount: "",
                                            date: "",
                                            expenseType: "",
                                            invoiceNo: "",
                                            paymentMode: "",
                                            storeId: initialStoreId,
                                            storeName: "",
                                        });
                                    }} >
                                        cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <>
                            <div className='Expense-widgets'>
                                <div className="Expense-widgets-header">
                                </div>
                                <div className="add-Expense-buttons">
                                    <button className="add-Product-button" onClick={() => {
                                        setIsAddExpense(true);
                                        setIsEditMode(false);
                                        setFormData({
                                            expenseName: "",
                                            otherExpenseName: "",
                                            description: "",
                                            amount: "",
                                            date: "",
                                            expenseType: "",
                                            invoiceNo: "",
                                            paymentMode: "",
                                            storeId: initialStoreId,
                                            storeName: "",
                                        });
                                    }}>+ Add Expense</button>
                                </div>
                            </div>
                            <Box component={Paper}>
                                <TableContainer component={Paper} sx={{
                                    maxHeight: 400,
                                    overflowY: "auto",
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                }}>
                                    <Table className="custom-product-table"
                                        sx={{
                                            '& td, & th': {
                                                border: 'none',
                                            },
                                            border: 'none',
                                        }}>
                                        <TableHead className="custom-product-head">
                                            <TableRow>
                                                {headings.map((heading, index) => {
                                                    const isExpense = heading === "Expense";
                                                    const isDate = heading === "Date";
                                                    const isAmount = heading === "Amount";
                                                    const isPaymentMode = heading === "Payment Mode";
                                                    const isExpenseType = heading === "Expense Type";
                                                    const sortKey = isExpense
                                                        ? "expenseName" : isDate
                                                            ? "date"
                                                            : isAmount
                                                                ? "amount"
                                                                : isPaymentMode
                                                                    ? "paymentMode"
                                                                    : isExpenseType
                                                                        ? "expenseType"
                                                                        : null;

                                                    return (
                                                        <TableCell
                                                            key={index}
                                                            sx={{
                                                                border: "none",
                                                                padding: "12px 16px",
                                                                textAlign: "center",
                                                                position: "sticky",
                                                                top: 0,
                                                                zIndex: 0,
                                                                backgroundColor: '#3C0008'
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    gap: "4px",
                                                                    color: "#ffffff",
                                                                    fontSize: "14px",
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {heading}

                                                                {sortKey && (
                                                                    <span
                                                                        onClick={() => handleSort(sortKey)}
                                                                        style={{
                                                                            fontSize: "10px",
                                                                            color: "#fff",
                                                                            cursor: "pointer",
                                                                            marginLeft: "4px",
                                                                            userSelect: "none",
                                                                        }}
                                                                    >
                                                                        {sortField === sortKey
                                                                            ? sortOrder === 1
                                                                                ? "▲"
                                                                                : "▼"
                                                                            : "▲▼"}
                                                                    </span>
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>

                                        </TableHead>
                                        <TableBody>
                                            {!expenses || expenses.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={headings.length}>
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'center',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                padding: '20px',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            No expenses found.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                expenses.map((expense, index) => (
                                                    <TableRow key={expense._id}>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>
                                                                {(currentPage - 1) * (limit === 'all' ? 0 : limit) + index + 1}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>{expense.expenseName}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>{expense.description}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>
                                                                {expense.employeeId?.name || expense.employeeDetails?.name || expense.employeeId?.[0]?.name || "—"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>
                                                                {expense.date
                                                                    ? DateTime.fromISO(expense.date).toFormat("dd/MM/yyyy")
                                                                    : "—"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>₹{expense.amount}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>{expense.paymentMode}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>{expense.expenseType}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>
                                                                INV-{expense.invoiceNo || "Non-Billable"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                                                                <Tooltip title="View Expense">
                                                                    <Icon
                                                                        icon={plusIcon}
                                                                        width={22}
                                                                        height={22}
                                                                        style={{ cursor: "pointer" }}
                                                                        sx={{
                                                                            color: '#1e293b',
                                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                color: '#6366f1',
                                                                                transform: 'scale(1.2) rotate(15deg)',
                                                                                filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.3))'
                                                                            }
                                                                        }}
                                                                        onClick={() => handleView(expense)}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Edit Expense">
                                                                    <Icon
                                                                        icon={editIcon}
                                                                        width={20}
                                                                        height={20}
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handleEdit(expense)}
                                                                    />
                                                                </Tooltip>
                                                                {role !== "SelfAdmin" && role !== "Accountant" && (
                                                                    <Tooltip title="Delete Expense">
                                                                        <Icon icon={deleteIcon} width={20} height={20} style={{ color: '#C10000', cursor: "pointer" }}
                                                                            onClick={() => handleOpenDelete(expense._id)}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>

                                    </Table>
                                </TableContainer>
                                <div className="product-bottom">
                                    <div className="Expense-section show-entries">
                                        <span className="text-label">Show</span>
                                        <select className="entries-select" value={limit}
                                            onChange={handleItemsPerPageChange}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="all">All</option>
                                        </select>
                                        <span className="text-label">Entries</span>
                                    </div>
                                    <div className="Expense-section showing-range">
                                        {
                                            limit === 'all'
                                                ? `Showing all ${totalCount} entries`
                                                : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                                        }
                                    </div>
                                    <div className="Expense-section spacer"></div>
                                    <div className="Expense-section pagination-buttons">
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange("first")}
                                        ><Icon icon='mdi:chevron-double-left' /></button>
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange("prev")}
                                        ><Icon icon='mdi:chevron-left' /></button>
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange("next")}
                                        ><Icon icon='mdi:chevron-right' /></button>
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange("last")}
                                        ><Icon icon='mdi:chevron-double-right' /></button>
                                    </div>
                                </div>
                            </Box>
                            {limit > 5 && expenses.length > 5 && (
                                <div className="scroll-down-hint">
                                    <Typography sx={{ display: 'flex', justifyContent: 'center', marginTop: 2, fontSize: 14, fontWeight: 600 }}>
                                        Scroll down to see more Expenses
                                    </Typography>
                                </div>
                            )}
                        </>
                    )}
                </main>
                <Footer />
            </div>

            {isAddExpense && isEditMode && (
                <div className="popup-overlay">
                    <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                        <Icon
                            icon={closeIcon}
                            className="popup-close-icon-outside"
                            onClick={() => {
                                setIsAddExpense(false);
                                setIsEditMode(false);
                                setEditExpenseId(null);
                                setFormData({
                                    expenseName: "",
                                    otherExpenseName: "",
                                    description: "",
                                    amount: "",
                                    date: "",
                                    expenseType: "",
                                    invoiceNo: "",
                                    paymentMode: "",
                                    storeId: initialStoreId,
                                    storeName: "",
                                });
                            }}
                        />
                        <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                            <Typography variant="h6" textAlign={"center"} gutterBottom sx={{ color: '#191c24', mb: 4 }}>
                                {isEditMode ? "Edit Expense" : "Add Expense"}
                            </Typography>
                            <form className='Expense-form' onSubmit={handleAddExpense}>
                                <div className='Expense-form-row expense-popup-row'>
                                    <div className="Expense-form-group" style={{ flex: 1 }}>
                                        <label className="input-label">Store Name</label>
                                        <StoreMultiSelect
                                            value={
                                                formData.storeId
                                                    ? [{
                                                        value: formData.storeId,
                                                        label: formData.storeName
                                                    }]
                                                    : []
                                            }
                                            onChange={(val) => {
                                                if (adminStoreIds.length === 1) return;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    storeId: val?.[0]?.value || "",
                                                    storeName: val?.[0]?.label || "",
                                                }));
                                            }}
                                            loadStores={loadStores}
                                            loggedInRole={role}
                                            assignedStoreIds={adminStoreIds}
                                            isDisabled={adminStoreIds.length === 1}
                                        />
                                    </div>
                                    <div className="Expense-form-group" style={{ flex: 1 }}>
                                        <label className="input-label">Expense</label>
                                        <CreatableSelect
                                            components={makeAnimated()}
                                            options={expenseOptions}
                                            placeholder="Select or type expense"
                                            isClearable
                                            isSearchable
                                            value={
                                                expenseOptions.find(option => option.value === formData.expenseName)
                                                || (formData.expenseName
                                                    ? { value: formData.expenseName, label: formData.expenseName }
                                                    : null)
                                            }
                                            onChange={(selectedOption) => {
                                                const selectedValue = selectedOption ? selectedOption.value : "";
                                                setFormData(prev => ({
                                                    ...prev,
                                                    expenseName: selectedValue,
                                                    employeeId: ["salary", "incentives", "OT"].includes(selectedValue)
                                                        ? prev.employeeId
                                                        : "",
                                                    employeeName: ["salary", "incentives", "OT"].includes(selectedValue)
                                                        ? prev.employeeName
                                                        : ""
                                                }));
                                            }}
                                        />
                                        {errors.expenseName && <p className="field-error">{errors.expenseName}</p>}
                                    </div>
                                    <div className="Expense-form-group" style={{ flex: 1 }}>
                                        <label className="input-label">Date</label>
                                        <input
                                            type="date"
                                            className="Expense-input"
                                            value={formData.date || ""}
                                            disabled={!formData.expenseName}
                                            onChange={(e) => handleExpenseDateChangeWithYearCheck(e.target.value)}
                                        />
                                        {errors.date && <p className="field-error">{errors.date}</p>}
                                    </div>
                                </div>

                                {formData.expenseName === 'other' && (
                                     <div className='Expense-form-row expense-popup-row'>
                                        <div className="Expense-form-group" style={{ flex: 1 }}>
                                            <label className="input-label">Other Expense Name</label>
                                            <input
                                                className="Expense-input"
                                                placeholder="Specify the expense"
                                                value={formData.otherExpenseName || ""}
                                                onChange={(e) => setFormData(prev => ({ ...prev, otherExpenseName: e.target.value }))}
                                            />
                                            {errors.otherExpenseName && <p className="field-error">{errors.otherExpenseName}</p>}
                                        </div>
                                    </div>
                                )}

                                {["salary", "incentives", "OT"].includes(formData.expenseName) && (
                                     <div className='Expense-form-row expense-popup-row'>
                                        <div className="Expense-form-group" style={{ flex: 1 }}>
                                            <label className="input-label">Employee</label>
                                            <EmployeeSelect
                                                key={`${formData.storeId}-${employeeRefreshKey}`}
                                                loadEmployees={loadEmployees}
                                                value={
                                                    formData.employeeId
                                                        ? { value: formData.employeeId, label: formData.employeeName }
                                                        : null
                                                }
                                                onAddEmployee={() => setIsAddEmployee(true)}
                                                onChange={(selectedOption) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        employeeId: selectedOption ? selectedOption.value : "",
                                                        employeeName: selectedOption ? selectedOption.label : ""
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className='Expense-form-row expense-popup-row'>
                                    <div className='Expense-form-group' style={{ flex: 1 }}>
                                        <label className="input-label">Expense Type</label>
                                        <select
                                            className="Expense-input"
                                            required
                                            disabled={!formData.expenseName}
                                            value={formData.expenseType}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, expenseType: e.target.value }))}
                                        >
                                            <option value='' >Select Expense Type</option>
                                            <option value='Billable'>Receiptable</option>
                                            <option value='Non-Billable'>Non-Receiptable</option>
                                        </select>
                                        {errors.expenseType && <p className="field-error">{errors.expenseType}</p>}
                                    </div>
                                    {formData.expenseType === "Billable" && (
                                        <div className='Expense-form-group' style={{ flex: 1 }}>
                                            <label className='input-label'>Invoice No</label>
                                            <input
                                                className="Expense-input"
                                                value={formData.invoiceNo || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*$/.test(value)) setFormData((prev) => ({ ...prev, invoiceNo: value }));
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className='Expense-form-group' style={{ flex: 1 }}>
                                        <label className='input-label'>Amount</label>
                                        <input
                                            className="Expense-input"
                                            disabled={!formData.expenseType}
                                            value={formData.amount || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*\.?\d{0,2}$/.test(value)) setFormData((prev) => ({ ...prev, amount: value }));
                                            }}
                                        />
                                        {errors.amount && <p className="field-error">{errors.amount}</p>}
                                    </div>
                                    <div className='Expense-form-group' style={{ flex: 1 }}>
                                        <label className='input-label'>Payment Mode</label>
                                        <select
                                            className="Expense-input"
                                            required
                                            disabled={!formData.amount}
                                            value={formData.paymentMode}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, paymentMode: e.target.value }))}
                                        >
                                            <option value='' >Select Payment Mode</option>
                                            <option value='CASH'>Cash</option>
                                            <option value='CARD'>Card</option>
                                            <option value='UPI'>UPI</option>
                                            <option value='OTHERS'>Others</option>
                                        </select>
                                        {errors.paymentMode && <p className="field-error">{errors.paymentMode}</p>}
                                    </div>
                                </div>
                                <div className='Expense-form-row' style={{ marginBottom: '20px' }}>
                                    <div className='Expense-form-group' style={{ width: '100%' }}>
                                        <label className='input-label'>Description</label>
                                        <textarea
                                            placeholder="Enter description"
                                            className="Expense-input"
                                            value={formData.description || ""}
                                            onChange={e => {
                                                let value = e.target.value;
                                                if (value.endsWith("\n")) value += "• ";
                                                setFormData(prev => ({ ...prev, description: value }));
                                            }}
                                            style={{ height: '100px' }}
                                        />
                                        {errors.description && <p className="field-error">{errors.description}</p>}
                                    </div>
                                </div>
                                <div className="Expense-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '20px' }}>
                                    <button className="add-Product-button" type="submit" onClick={handleUpdateExpense}>
                                        Update Expense
                                    </button>
                                    <button type="button" className="cancel-Product-button" onClick={() => {
                                        setIsAddExpense(false);
                                        setIsEditMode(false);
                                        setEditExpenseId(null);
                                        setFormData({
                                            expenseName: "",
                                            otherExpenseName: "",
                                            description: "",
                                            amount: "",
                                            date: "",
                                            expenseType: "",
                                            invoiceNo: "",
                                            paymentMode: "",
                                            storeId: initialStoreId,
                                            storeName: "",
                                        });
                                    }}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                open={openDeleteModal}
                onClose={handleCloseDelete}
                aria-labelledby="modal-modal-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 380,
                        background: "linear-gradient(135deg, #ffffff, #faf5f6)",
                        borderRadius: "20px",
                        boxShadow: "0 25px 70px rgba(0,0,0,0.35)",
                        p: 4,
                        outline: "none",
                        textAlign: "center",
                        overflow: "hidden"
                    }}
                >
                    <Box
                        sx={{
                            width: 74,
                            height: 74,
                            borderRadius: "50%",
                            background: "#3C0008",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 16px auto",
                            color: "#fff",
                            fontSize: 30,
                            boxShadow: "0 12px 28px rgba(26,30,39,0.55)",
                            animation: "deletePulse 1.6s infinite ease-in-out",
                            "@keyframes deletePulse": {
                                "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(26,30,39,0.7)" },
                                "70%": { transform: "scale(1.1)", boxShadow: "0 0 0 18px rgba(26,30,39,0)" },
                                "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(26,30,39,0)" },
                            },
                        }}
                    >
                        🗑️
                    </Box>
                    <Typography id="modal-modal-title" variant="h6" sx={{ fontWeight: 700, color: "#2b2b2b" }}>
                        Confirm Delete
                    </Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 1.2, fontSize: 14, color: "#666" }}>
                        Are you sure you want to delete this expense?
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mt: 3.5 }}>
                        <button onClick={handleDelete} className="add-Product-button">Yes, Delete</button>
                        <button onClick={handleCloseDelete} className="cancel-Product-button">Cancel</button>
                    </Box>
                </Box>
            </Modal>

            {/* View Details Popup */}
            <ExpenseDetailsPopup
                open={isViewMode}
                onClose={() => setIsViewMode(false)}
                expense={viewExpenseData}
            />
            {isAddStore && (
                <div className="popup-overlay">
                    <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                        <Icon
                            icon={closeIcon}
                            className="popup-close-icon-outside"
                            onClick={() => setIsAddStore(false)}
                        />
                        <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                            <AddStoreForm
                                onSuccess={() => {
                                    setIsAddStore(false);
                                    setStoreRefreshKey(prev => prev + 1);
                                }}
                                onCancel={() => setIsAddStore(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
            {isAddEmployee && (
                <div className="popup-overlay">
                    <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                        <Icon
                            icon={closeIcon}
                            className="popup-close-icon-outside"
                            onClick={() => setIsAddEmployee(false)}
                        />
                        <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                            <AddEmployeeForm
                                onSuccess={() => {
                                    setIsAddEmployee(false);
                                    setEmployeeRefreshKey(prev => prev + 1);
                                }}
                                onCancel={() => setIsAddEmployee(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
export default ExpensePage;